import { createStore, createEvent, createEffect, combine } from 'effector';
import request from '@helpers/request';
import api from '@constants/endpoints';
import { PaginationProps } from 'antd/lib/pagination';
import createPreloadersStore from './preloaders';
import createPaginationStore from './pagination';
import createFiltersStore from './filters';
import localeWeight, { LOCALE } from './localeWeight';
import IStatus from '@interfaces/IStatus';

interface ISpamStatusesPrefetchedPage {
	page: number;
	pageSize: number;
	total: number;
	items: IStatus[];
	cacheKey: string;
}

interface ISpamStatusesPrefetchRequest {
	page: number;
	pageSize: number;
	cacheKey: string;
	isLoading: boolean;
}

interface IApplyPrefetchedPage extends ISpamStatusesPrefetchedPage {
	additions: IStatus[];
}

export const reset = createEvent('Statuses Spam store reset');
export const clearPrefetchedPage = createEvent('Clear prefetched spam statuses page');
export const resetFastPrefetch = createEvent('Reset fast spam statuses prefetch');

const applyPrefetchedPage = createEvent<IApplyPrefetchedPage>(
	'Apply prefetched spam statuses page',
);

export const DEFAULT_SPAM_STATUSES_FILTERS = {
	nickname: '',
	statusAuthorId: '',
};

const defaultFilters = {
	...DEFAULT_SPAM_STATUSES_FILTERS,
};

const getLocaleValue = () =>
	localeWeight.getState() === LOCALE.ALL ? undefined : localeWeight.getState();

const buildCacheKey = ({
	filters,
	locale,
	pageSize,
}: {
	filters: Record<string, unknown>;
	locale?: string;
	pageSize?: number;
}): string =>
	JSON.stringify({
		...filters,
		locale,
		pageSize,
	});

const getCurrentCacheKey = (pageSize?: number): string =>
	buildCacheKey({
		filters: filters.getState(),
		locale: getLocaleValue(),
		pageSize,
	});

export const getList = createEffect({
	name: 'getList',
	async handler() {
		const { current, pageSize } = pagination.getState();
		const { nickname, statusAuthorId } = filters.getState();
		const response = await request(api.spam.getStatuses, {
			data: {
				locale: getLocaleValue(),
				pagination: {
					type: 'PAGE',
					pageSize,
					page: (current || 1) - 1,
				},
				nickname,
				statusAuthorId,
			},
		});
		return response;
	},
});

export const prefetchPage = createEffect({
	name: 'prefetchSpamStatusesPage',
	async handler(page: number): Promise<ISpamStatusesPrefetchedPage> {
		const { pageSize }: PaginationProps = pagination.getState();
		const filtersParams = filters.getState();
		const { nickname, statusAuthorId } = filtersParams;
		const locale = getLocaleValue();
		const response = await request(api.spam.getStatuses, {
			data: {
				locale,
				pagination: {
					type: 'PAGE',
					pageSize,
					page: page - 1,
				},
				nickname,
				statusAuthorId,
			},
		});

		const claimedStatuses = response.data.body.claimedStatuses;

		return {
			page,
			pageSize: pageSize || 0,
			total: claimedStatuses.total || 0,
			items: claimedStatuses.page || [],
			cacheKey: buildCacheKey({
				filters: filtersParams,
				locale,
				pageSize,
			}),
		};
	},
});

interface ModerateStatusParams {
	statusId: IStatus['id'];
	preloaderId: IStatus['id'];
}

export const block = createEffect({
	name: 'block',
	async handler({ statusId }: ModerateStatusParams) {
		const response = await request(api.spam.approveStatus, {
			urlParams: { statusId },
			data: {
				isSpam: true
			}
		});
		return response;
	},
});

export const skip = createEffect({
	name: 'skip',
	async handler({ statusId }: ModerateStatusParams) {
		const response = await request(api.spam.approveStatus, {
			urlParams: { statusId },
			data: {
				isSpam: false
			}
		});
		return response;
	},
});

const { pagination, setPagination } = createPaginationStore({
	getList,
	listName: 'statuses',
	resetEvent: reset,
	bodyPath: ['data', 'body', 'claimedStatuses'],
});

export { setPagination };

const preloaders = createPreloadersStore([getList, block, skip, prefetchPage]);

const { filters, setFilters } = createFiltersStore({
	listName: 'statuses',
	deaultValues: defaultFilters,
	resetEvent: reset,
});

export { setFilters };

const isRelevantPrefetchedPage = (pageData: ISpamStatusesPrefetchedPage | null): boolean => {
	if (!pageData) {
		return false;
	}

	const { current, pageSize } = pagination.getState();

	return (
		pageData.page === (current || 1) + 1 &&
		pageData.pageSize === pageSize &&
		pageData.cacheKey === getCurrentCacheKey(pageSize) &&
		pageData.items.length > 0
	);
};

const prefetchedPageRequest = createStore<ISpamStatusesPrefetchRequest | null>(null)
	.on(prefetchPage, (_, page) => {
		const pageSize = pagination.getState().pageSize || 0;

		return {
			page,
			pageSize,
			cacheKey: getCurrentCacheKey(pageSize),
			isLoading: true,
		};
	})
	.on(prefetchPage.done, (state) => state ? { ...state, isLoading: false } : null)
	.on(prefetchPage.fail, (state) => state ? { ...state, isLoading: false } : null)
	.on(clearPrefetchedPage, () => null)
	.on(getList, () => null)
	.reset(reset);

const shouldPrefetchNextPageFast = createStore(false)
	.on(getList.done, () => true)
	.on(block.done, () => true)
	.on(skip.done, () => true)
	.on(prefetchPage.done, () => false)
	.on(resetFastPrefetch, () => false)
	.on(clearPrefetchedPage, () => false)
	.reset(reset);

const prefetchedPage = createStore<ISpamStatusesPrefetchedPage | null>(null)
	.on(applyPrefetchedPage, (_, { page, pageSize, total, items, cacheKey }) => ({
		page,
		pageSize,
		total,
		items,
		cacheKey,
	}))
	.on(clearPrefetchedPage, () => null)
	.on(getList, () => null)
	.on(block.done, (state) => {
		if (!state || !isRelevantPrefetchedPage(state)) {
			return null;
		}

		return {
			...state,
			total: Math.max(state.total - 1, 0),
			items: state.items.slice(1),
		};
	})
	.on(skip.done, (state) => {
		if (!state || !isRelevantPrefetchedPage(state)) {
			return null;
		}

		return {
			...state,
			total: Math.max(state.total - 1, 0),
			items: state.items.slice(1),
		};
	})
	.reset(reset);

const fillListFromPrefetchedPage = (
	state: IStatus[],
	statusId: IStatus['id'],
): IStatus[] => {
	const nextState = state.filter((status) => status.id !== statusId);
	const currentPrefetchedPage = prefetchedPage.getState();
	const currentPagination = pagination.getState();

	setPagination({
		...currentPagination,
		total: Math.max((currentPagination.total || 0) - 1, 0),
	});

	if (!isRelevantPrefetchedPage(currentPrefetchedPage)) {
		return nextState;
	}

	const nextStatus = currentPrefetchedPage?.items[0];

	if (!nextStatus || nextState.some((status) => status.id === nextStatus.id)) {
		return nextState;
	}

	return [...nextState, nextStatus].slice(
		0,
		currentPagination.pageSize || nextState.length + 1,
	);
};

const list = createStore<IStatus[]>([])
	.on(getList.done, (_, { result }) => result.data.body.claimedStatuses.page)
	.on(applyPrefetchedPage, (state, { additions }) => [...state, ...additions])
	.on(block.done, (state, { params }) => fillListFromPrefetchedPage(state, params.statusId))
	.on(skip.done, (state, { params }) => fillListFromPrefetchedPage(state, params.statusId))
	.reset(reset);

prefetchPage.done.watch(({ result }) => {
	const { current, pageSize } = pagination.getState();

	if (
		result.page !== (current || 1) + 1 ||
		result.pageSize !== pageSize ||
		result.cacheKey !== getCurrentCacheKey(pageSize)
	) {
		return;
	}

	const currentList = list.getState();
	const currentIds = new Set(currentList.map((status) => status.id));
	const availableItems = result.items.filter((status) => !currentIds.has(status.id));
	const missingItemsCount = Math.max((pageSize || 0) - currentList.length, 0);
	const additions = availableItems.slice(0, missingItemsCount);
	const additionIds = new Set(additions.map((status) => status.id));

	applyPrefetchedPage({
		...result,
		additions,
		items: availableItems.filter((status) => !additionIds.has(status.id)),
	});
});

const spamStatuses = combine({
	preloaders,
	list,
	pagination,
	setPagination,
	filters,
	setFilters,
	prefetchedPage,
	prefetchedPageRequest,
	shouldPrefetchNextPageFast,
});

export default spamStatuses;
